import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { 
  MdWork, MdLocationOn, MdAttachMoney, 
  MdSend, MdCheckCircle, MdAddCircle, MdClose, MdPeople, MdEmail, MdPhone,
  MdCloudUpload, MdPictureAsPdf, MdAccountCircle, MdCheck, MdInfo, MdCancel
} from "react-icons/md";
import AppHeader from "./AppHeader";
import { db, auth, storage } from "./firebase";
import { 
  collection, addDoc, getDocs, query, 
  orderBy, serverTimestamp, where, getDoc, doc, updateDoc 
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";

const Container = styled.div`
  max-width: 800px;
  margin: 10px auto;
  padding: 0 10px;

  @media (min-width: 768px) {
    margin: 20px auto;
    padding: 0 15px;
  }
`;

const JobCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e0e0e0;

  @media (min-width: 768px) {
    padding: 20px;
    margin-bottom: 20px;
  }
`;

const PostJobCard = styled(JobCard)`
  background: #f8f9ff;
  border: 2px dashed #1a73e8;
`;

const Title = styled.h2`
  margin: 0 0 5px 0;
  color: #1a73e8;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.2rem;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const BusinessName = styled.div`
  font-weight: 600;
  color: #555;
  margin-bottom: 12px;
  font-size: 13px;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
  color: #666;
  font-size: 13px;

  @media (min-width: 768px) {
    gap: 20px;
    font-size: 14px;
  }

  span {
    display: flex;
    align-items: center;
    svg { margin-right: 5px; color: #1a73e8; }
  }
`;

const ApplyButton = styled.button`
  background: #1a73e8;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
`;

const StatusCheckButton = styled.button`
  background: #f0f2f5;
  color: #1a73e8;
  border: 1px solid #1a73e8;
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
`;

const ViewAppsButton = styled.button`
  background: #f0f2f5;
  color: #1c1e21;
  border: 1px solid #dddfe2;
  padding: 10px 15px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 6px;
  border: 1px solid #ccc;
  box-sizing: border-box;
  font-size: 14px;
`;

const FileLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px dashed #1a73e8;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 10px;
  font-size: 14px;
  color: #1a73e8;
  background: #fff;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 6px;
  border: 1px solid #ccc;
  min-height: 100px;
  box-sizing: border-box;
  font-size: 14px;
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.6); display: flex; justify-content: center; align-items: center; z-index: 2000;
`;

const ModalContent = styled.div`
  background: white; padding: 20px; border-radius: 12px; width: 95%; max-width: 550px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2); position: relative;
  max-height: 90vh; overflow-y: auto;

  @media (min-width: 768px) {
    padding: 25px;
    width: 90%;
  }
`;

const ApplicantItem = styled.div`
  border: 1px solid #eee;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${props => props.status === 'Accepted' ? '#f0fff4' : props.status === 'Rejected' ? '#fff5f5' : '#fafafa'};
  
  .name { 
    font-weight: bold; 
    font-size: 15px; 
    display: flex; 
    align-items: center; 
    justify-content: space-between;
    @media (min-width: 768px) { font-size: 16px; }
  }
  
  .action-links { 
    display: flex; 
    flex-direction: column; 
    gap: 8px; 
    margin-top: 10px;
    @media (min-width: 480px) { flex-direction: row; }
  }
`;

const ActionBtn = styled.button`
  border: none; padding: 10px; border-radius: 6px; font-weight: bold; cursor: pointer; flex: 1;
  display: flex; align-items: center; justify-content: center; gap: 5px; color: white;
  background: ${props => props.color || '#1a73e8'};
  font-size: 13px;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const StatusMessage = styled.div`
  padding: 15px; border-radius: 8px; text-align: center; line-height: 1.5;
  font-weight: 600; font-size: 14px;
  background: ${props => props.type === 'Accepted' ? '#e6fffa' : props.type === 'Rejected' ? '#fff5f5' : '#ebf8ff'};
  color: ${props => props.type === 'Accepted' ? '#2c7a7b' : props.type === 'Rejected' ? '#c53030' : '#2b6cb0'};
  border: 1px solid ${props => props.type === 'Accepted' ? '#81e6d9' : props.type === 'Rejected' ? '#feb2b2' : '#90cdf4'};

  @media (min-width: 768px) {
    padding: 20px;
    font-size: 16px;
  }
`;

const FileLink = styled.a`
  flex: 1; display: flex; align-items: center; justify-content: center;
  gap: 8px; padding: 10px; background: white; border: 1px solid #1a73e8;
  color: #1a73e8; border-radius: 6px; text-decoration: none; font-size: 13px; font-weight: 600;
`;

const JobsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isBusinessOwner, setIsBusinessOwner] = useState(false);
  const [myBusinessData, setMyBusinessData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [appliedJobsData, setAppliedJobsData] = useState({});
  const [showForm, setShowForm] = useState(false);

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showAppsModal, setShowAppsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  const [applicantInfo, setApplicantInfo] = useState({ fullName: "", email: "", phone: "" });
  const [resumeFile, setResumeFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [jobForm, setJobForm] = useState({ title: "", salary: "", location: "", description: "" });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const busDoc = await getDoc(doc(db, "businessPages", user.uid));
        if (busDoc.exists()) {
          setIsBusinessOwner(true);
          setMyBusinessData(busDoc.data());
        }
        fetchJobs(user.uid);
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [location.search, navigate]);

  const fetchJobs = async (userId) => {
    const q = query(collection(db, "allJobs"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    const jobsList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const params = new URLSearchParams(location.search);
    const searchTerm = params.get("search");

    if (searchTerm) {
      const termLower = searchTerm.toLowerCase();
      const matched = jobsList.filter(j => j.title?.toLowerCase().includes(termLower));
      const others = jobsList.filter(j => !j.title?.toLowerCase().includes(termLower));
      setJobs([...matched, ...others]);
    } else {
      setJobs(jobsList);
    }

    const appliedInfo = {};
    for (const job of jobsList) {
      const appSnap = await getDocs(query(collection(db, "allJobs", job.id, "applications"), where("userId", "==", userId)));
      if (!appSnap.empty) {
        appliedInfo[job.id] = appSnap.docs[0].data();
      }
    }
    setAppliedJobsData(appliedInfo);
  };

  const fetchApplicants = async (jobId) => {
    const q = query(collection(db, "allJobs", jobId, "applications"), orderBy("appliedAt", "desc"));
    const snap = await getDocs(q);
    setApplicants(snap.docs.map(doc => ({ id: doc.id, jobId, ...doc.data() })));
    setShowAppsModal(true);
  };

  const updateStatus = async (appId, jobId, newStatus) => {
    try {
      const appRef = doc(db, "allJobs", jobId, "applications", appId);
      await updateDoc(appRef, { status: newStatus });
      alert(`Application ${newStatus}!`);
      fetchApplicants(jobId);
    } catch (err) { console.error(err); }
  };

  const uploadFile = async (file, folder) => {
    if (!file) return null;
    const storageRef = ref(storage, `jobApplications/${folder}/${Date.now()}_${file.name}`);
    const uploadTask = await uploadBytesResumable(storageRef, file);
    return await getDownloadURL(uploadTask.ref);
  };

  const handlePostJob = async (e) => {
    e.preventDefault();
    if (!jobForm.title || !jobForm.location) return alert("Please fill details");
    try {
      await addDoc(collection(db, "allJobs"), {
        ...jobForm,
        businessId: currentUser.uid,
        businessName: myBusinessData.businessName,
        businessLogo: myBusinessData.logoUrl,
        createdAt: serverTimestamp(),
      });
      alert("Job Posted!");
      setJobForm({ title: "", salary: "", location: "", description: "" });
      setShowForm(false);
      fetchJobs(currentUser.uid);
    } catch (err) { console.error(err); }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!resumeFile || !photoFile) return alert("Please upload both files");
    setIsUploading(true);
    try {
      const resumeUrl = await uploadFile(resumeFile, "resumes");
      const photoUrl = await uploadFile(photoFile, "photos");
      await addDoc(collection(db, "allJobs", selectedJob.id, "applications"), {
        userId: currentUser.uid,
        ...applicantInfo,
        resumeUrl, photoUrl,
        status: "Pending",
        appliedAt: serverTimestamp()
      });
      setShowApplyModal(false);
      setIsUploading(false);
      alert("Applied Successfully!");
      fetchJobs(currentUser.uid);
    } catch (err) { console.error(err); setIsUploading(false); }
  };

  const renderStatusMessage = (status) => {
    if (status === "Pending") return "Your Application will be reviewed soon";
    if (status === "Accepted") return "Congratulations Applications Accepted further details will send to you through mail";
    if (status === "Rejected") return "Come back with correct Recruitment";
    return "";
  };

  return (
    <div style={{background: "#f0f2f5", minHeight: "100vh"}}>
      <AppHeader />
      <Container>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <h1 style={{fontSize: '1.5rem'}}>Explore Jobs</h1>
          {isBusinessOwner && (
            <button onClick={() => setShowForm(!showForm)} style={{background: '#1a73e8', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize:'13px', display:'flex', alignItems:'center', gap:'5px'}}>
              <MdAddCircle /> {showForm ? "Cancel" : "Post Job"}
            </button>
          )}
        </div>

        {showForm && (
          <PostJobCard>
            <h3 style={{marginTop: 0}}>Post a New Vacancy</h3>
            <Input placeholder="Job Title" value={jobForm.title} onChange={e => setJobForm({...jobForm, title: e.target.value})} />
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
              <Input placeholder="Salary" value={jobForm.salary} onChange={e => setJobForm({...jobForm, salary: e.target.value})} />
              <Input placeholder="Location" value={jobForm.location} onChange={e => setJobForm({...jobForm, location: e.target.value})} />
            </div>
            <TextArea placeholder="Job Description" value={jobForm.description} onChange={e => setJobForm({...jobForm, description: e.target.value})} />
            <ApplyButton onClick={handlePostJob}>Publish Job</ApplyButton>
          </PostJobCard>
        )}

        {jobs.map(job => (
          <JobCard key={job.id}>
            <div style={{display:'flex', gap:'12px', alignItems:'center'}}>
               <img src={job.businessLogo || "/default-business.png"} style={{width:'45px', height:'45px', borderRadius:'8px', objectFit:'cover'}} alt="logo" />
               <div>
                  <Title><MdWork size={20} /> {job.title}</Title>
                  <BusinessName>{job.businessName}</BusinessName>
               </div>
            </div>
            <InfoRow>
              <span><MdLocationOn /> {job.location}</span>
              <span><MdAttachMoney /> {job.salary}</span>
            </InfoRow>

            <p style={{whiteSpace: 'pre-wrap', color: '#444', fontSize: '14px', lineHeight: '1.5', margin: '12px 0'}}>
              {job.description}
            </p>
            
            {currentUser?.uid !== job.businessId ? (
              <>
                {!appliedJobsData[job.id] ? (
                  <ApplyButton onClick={() => {setSelectedJob(job); setShowApplyModal(true);}}>
                    <MdSend /> Apply Now
                  </ApplyButton>
                ) : (
                  <StatusCheckButton onClick={() => {setSelectedJob(job); setShowStatusModal(true);}}>
                    <MdInfo /> View Status
                  </StatusCheckButton>
                )}
              </>
            ) : (
              <ViewAppsButton onClick={() => fetchApplicants(job.id)}>
                <MdPeople /> View Applications
              </ViewAppsButton>
            )}
          </JobCard>
        ))}
      </Container>

      {showStatusModal && (
        <ModalOverlay onClick={() => setShowStatusModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
             <MdClose size={24} style={{position:'absolute', top:'15px', right:'15px', cursor:'pointer'}} onClick={() => setShowStatusModal(false)} />
             <h3>Application Status</h3>
             <StatusMessage type={appliedJobsData[selectedJob?.id]?.status}>
               {renderStatusMessage(appliedJobsData[selectedJob?.id]?.status)}
             </StatusMessage>
          </ModalContent>
        </ModalOverlay>
      )}

      {showAppsModal && (
        <ModalOverlay onClick={() => setShowAppsModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
              <h3 style={{margin:0}}>Applicants</h3>
              <MdClose size={24} style={{cursor:'pointer'}} onClick={() => setShowAppsModal(false)} />
            </div>
            {applicants.map((app, i) => (
              <ApplicantItem key={i} status={app.status}>
                <div className="name">
                  {app.fullName} 
                  <span style={{fontSize:'11px', background:'#eee', padding:'2px 8px', borderRadius:'10px'}}>{app.status}</span>
                </div>
                <div className="action-links">
                  <FileLink href={app.resumeUrl} target="_blank"><MdPictureAsPdf /> Resume</FileLink>
                  <FileLink href={app.photoUrl} target="_blank"><MdAccountCircle /> Photo</FileLink>
                </div>
                <div style={{marginTop: '10px', display: 'flex', gap: '8px'}}>
                  <ActionBtn color="#4caf50" disabled={app.status === 'Accepted'} onClick={() => updateStatus(app.id, app.jobId, 'Accepted')}>
                    <MdCheck /> Accept
                  </ActionBtn>
                  <ActionBtn color="#f44336" disabled={app.status === 'Rejected'} onClick={() => updateStatus(app.id, app.jobId, 'Rejected')}>
                    <MdCancel /> Reject
                  </ActionBtn>
                </div>
              </ApplicantItem>
            ))}
          </ModalContent>
        </ModalOverlay>
      )}

      {showApplyModal && (
        <ModalOverlay>
          <ModalContent>
            <MdClose size={24} style={{position:'absolute', top:'15px', right:'15px', cursor:'pointer'}} onClick={() => setShowApplyModal(false)} />
            <h3 style={{fontSize: '1.2rem'}}>Apply for {selectedJob?.title}</h3>
            <form onSubmit={submitApplication}>
              <Input required placeholder="Full Name" value={applicantInfo.fullName} onChange={e => setApplicantInfo({...applicantInfo, fullName: e.target.value})} />
              <Input required type="email" placeholder="Email" value={applicantInfo.email} onChange={e => setApplicantInfo({...applicantInfo, email: e.target.value})} />
              <Input required type="tel" placeholder="Phone" value={applicantInfo.phone} onChange={e => setApplicantInfo({...applicantInfo, phone: e.target.value})} />
              <p style={{fontSize: '11px', fontWeight: 'bold', marginBottom: '5px'}}>Resume (PDF)</p>
              <FileLabel><MdCloudUpload /> <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{resumeFile ? resumeFile.name : "Choose Resume"}</span><input type="file" hidden accept=".pdf" onChange={e => setResumeFile(e.target.files[0])} /></FileLabel>
              <p style={{fontSize: '11px', fontWeight: 'bold', marginBottom: '5px'}}>Photo</p>
              <FileLabel><MdCloudUpload /> <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{photoFile ? photoFile.name : "Choose Photo"}</span><input type="file" hidden accept="image/*" onChange={e => setPhotoFile(e.target.files[0])} /></FileLabel>
              <ApplyButton type="submit" disabled={isUploading}>{isUploading ? "Uploading..." : "Submit Application"}</ApplyButton>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  );
};

export default JobsPage;